import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getConsent, gpcEnabled, onConsentChange, setConsent, type ConsentState } from "@/lib/consent";
import ConsentPreferences from "./ConsentPreferences";

const HIDDEN_ON = ["/admin", "/privacy-choices"];

/**
 * Cookie / tracking consent (see src/lib/consent.ts for the model).
 * - Opt-in regions (EEA / UK / CH / NO): a bottom bar with equally weighted
 *   Accept all / Reject all / Customize. Nothing tracks until they choose.
 * - Everywhere else: a small notice in the floating-actions stack with OK and
 *   a link to change choices. Tracking runs unless they opt out.
 * Shown until the visitor makes a choice; decided on mount only, so the
 * prerendered HTML never contains it (no hydration mismatch).
 */
export default function ConsentBanner() {
  const [state, setState] = useState<ConsentState | null>(null);
  const [customize, setCustomize] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setState(getConsent());
    return onConsentChange(setState);
  }, []);

  if (!state || state.decided || HIDDEN_ON.some((p) => pathname.startsWith(p))) return null;

  if (!state.required) {
    return (
      <div role="region" aria-label="Privacy notice"
        className="w-full max-w-xs rounded-lg border border-border bg-card p-3 text-xs text-foreground shadow-lg">
        <p>
          We use cookies and similar tools for analytics and advertising measurement, including linking pages you
          viewed to forms you send us.{" "}
          <Link to="/cookie-notice/" className="text-primary underline">Cookie notice</Link>
        </p>
        <div className="mt-2 flex items-center gap-3">
          <button type="button" onClick={() => setConsent({ analytics: state.analytics, marketing: state.marketing && !gpcEnabled() })}
            className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            OK
          </button>
          <Link to="/privacy-choices/" className="text-xs text-primary underline">Your Privacy Choices</Link>
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="false" aria-labelledby="consent-title"
      className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto border-t border-border bg-card p-4 text-sm text-foreground shadow-2xl md:p-6">
      <div className="container max-w-4xl">
        <h2 id="consent-title" className="font-heading text-base font-bold">Your privacy choices</h2>
        <p className="mt-1 text-muted-foreground">
          With your permission we use analytics cookies (Google Analytics and our own) to understand how visitors find
          and use this site, and marketing cookies to measure advertising. Nothing is collected unless you accept.
          You can change this any time from &ldquo;Your Privacy Choices&rdquo; at the bottom of every page.{" "}
          <Link to="/cookie-notice/" className="text-primary underline">Cookie notice</Link>
          {" · "}
          <Link to="/privacy-policy/" className="text-primary underline">Privacy policy</Link>
        </p>
        {customize ? (
          <div className="mt-4"><ConsentPreferences /></div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={() => setConsent({ analytics: true, marketing: true })}
              className="rounded-md bg-primary px-4 py-2 font-semibold text-primary-foreground hover:bg-primary/90">
              Accept all
            </button>
            <button type="button" onClick={() => setConsent({ analytics: false, marketing: false })}
              className="rounded-md bg-primary px-4 py-2 font-semibold text-primary-foreground hover:bg-primary/90">
              Reject all
            </button>
            <button type="button" onClick={() => setCustomize(true)}
              className="rounded-md border border-border px-4 py-2 font-semibold hover:bg-muted">
              Customize
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
